import React, { useState, useEffect, Suspense } from "react";
import { render } from "react-dom";
import { connect, Provider, useSelector } from "react-redux";
import { Link, Redirect, Route, Switch, useLocation } from "react-router-dom";
//import { createBrowserHistory } from "history";
import { pathToRegexp, compile } from "path-to-regexp";
import { I18nextProvider, useTranslation, Trans } from "react-i18next";
import { setLocale, clearVotes } from "./actions/cartActions";
import "./assets/sass/app.scss";
import logo from "./assets/images/logo.png";
import hof_logo from "./assets/images/HOF-LOGO-transparent-white.png";
import hof_logo_2x from "./assets/images/HOF-LOGO-transparent-white@2x.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faTwitter,
  faYoutube,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import { CookieBanner } from "@palmabit/react-cookie-law";
import Cookies from "universal-cookie";
import Settings from "./constants/Settings";
import { Helmet } from "react-helmet";
import _ from "lodash";
import LanguageTools from "./utils/LanguageTools";
import API from "./api";

// Grommet
import {
  Box,
  Button,
  Collapsible,
  Footer,
  FormField,
  Grommet,
  Layer,
  ResponsiveContext,
  Select,
  Spinner,
  Text,
} from "grommet";
import { FormClose } from "grommet-icons";
import Theme from "./utils/Theme";

import i18n from "./i18n";

import { ConnectedRouter } from "connected-react-router";
import configureStore, { history } from "./reducers/configureStore";

// Utils
import { smallScreen } from "./utils/Misc";

// Components
import Usher from "./components/Usher";
import MyNav from "./components/Nav";
import VoteCart from "./components/VoteCart";
import NotFound from "./components/NotFound";
import LanguageSelector from "./components/utility/LanguageSelector";
import ScrollToTop from "./components/utility/ScrollToTop";

// Pages
import Home from "./pages/Home";
import Categories from "./pages/Categories";
import Judges from "./pages/Judges";
import Press from "./pages/Press";
import Committee from "./pages/Committee";
import GenericPage from "./pages/GenericPage";
import Museum from "./pages/Museum";
import NominatingProcess from "./pages/NominatingProcess";

// store
import { saveState } from "./utils/LocalStorage";
const store = configureStore({}); /* provide initial state if any */

// Cookies
const cookies = new Cookies();

// This method will save store state to local storage
store.subscribe(() => {
  saveState({
    cart: store.getState().cart,
  });
});

// i18n
const lang = i18n.language;

const generateLanguage = (locale, location) => {
  const ROUTE = "/:locale/:path*";
  const definePath = compile(ROUTE);
  const routeComponents = pathToRegexp(ROUTE).exec(location.pathname);

  let subPaths = null;
  if (routeComponents && routeComponents[2]) {
    subPaths = routeComponents[2].split("/");
  }

  return definePath({
    locale,
    path: subPaths,
  });
};

const changeLanguage = (lng, dispatch) => {
  i18n.changeLanguage(lng);
  setLanguageCookie(lng);
  if (dispatch) {
    dispatch(lng);
  }
  setTimeout(() => {
    window.location.reload();
  }, 500); // Reload to fetch new content from Contentful
};

// This function is bsacially here to allow page loads to en-EU to
// redirect to /eu. May be used in the future to allow more aliases and rewrites
const languagePathRewrites = (path) => {
  if (path.toLowerCase() === "eu") {
    return "en-eu";
  }
  if (path.toLowerCase() === "en-gb") {
    return "en-uk";
  }
  return path.toLowerCase();
};

const chooseInitialLanguage = (history) => {
  // Try and get language from url
  var path = history.location.pathname.split("/")[1];
  path = languagePathRewrites(path);
  if (Settings.languages.includes(path)) {
    i18n.changeLanguage(path);
    return path;
  }

  // Choose initial Language based on cookie of browser language
  // check for language cookie
  var languageCookie = getLanguageCookie();
  if (languageCookie && languageCookie !== "undefined") {
    console.debug("Found language cookie: " + languageCookie);
    i18n.changeLanguage(languageCookie);
    return languageCookie;
  } else {
    const sniffedLocale = LanguageTools.sniffLocale();
    console.debug("Sniffed locale: " + sniffedLocale);
    switch (sniffedLocale.toLowerCase()) {
      case "en-uk":
        i18n.changeLanguage("en-uk");
        return "en-uk";
      case "en-us":
        i18n.changeLanguage("en-us");
        return "en-us";
      case "en-eu":
        i18n.changeLanguage("en-eu");
        return "en-eu"; // EU uses uk language
      case "fr-fr":
        i18n.changeLanguage("fr-fr");
        return "fr-fr";
      case "de-de":
        i18n.changeLanguage("de-de");
        return "de-de";
      case "nl-nl":
        i18n.changeLanguage("nl-nl");
        return "nl-nl";
      case "it-it":
        i18n.changeLanguage("it-it");
        return "it-it";
      case "fr-be":
        i18n.changeLanguage("fr-be");
        return "fr-be";
      case "nl-be":
        i18n.changeLanguage("nl-be");
        return "nl-be";
      case "pt-pt":
        i18n.changeLanguage("pt-pt");
        return "pt-pt";
      case "es-es":
        i18n.changeLanguage("es-es");
        return "es-es";
    }
  }
  // Fallback
  return Settings.languageFallback;
};

const setLanguageCookie = (lng) => {
  cookies.set("language", lng, { path: process.env.REACT_APP_SUBFOLDER });
};

const getLanguageCookie = () => {
  return cookies.get("language")?.toLowerCase();
};

const currentDate = new Date();
const currentYear = currentDate.getFullYear();

let App = ({
  match,
  location,
  items,
  total,
  votes,
  voted,
  setLocale,
  clearVotes,
}) => {
  const [showNav, setShowNav] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const { t } = useTranslation();
  const SettingsDynamic = useSelector((state) => state.settings);

  /**
   * Returns a meta tag for content language based on the current language setting in i18n.
   * Checks the value of `i18n.language` and returns a corresponding meta tag with the content-language attribute set to the current language.
   * If the language does not match any of the specified cases, it defaults to returning a meta tag with the content-language attribute set to the current language.
   *
   * @returns {JSX.Element} A meta tag element with the content-language attribute set to the current language.
   */
  const getLanguageMetaTag = () => {
    if (i18n.language === "en-EU") {
      return <meta name="content-language" content={i18n.language} />;
    } else {
      return <meta name="content-language" content={i18n.language} />;
    }
  };

  /**
   * Returns the appropriate Facebook link based on the current language setting in i18n.
   * Checks the value of `i18n.language` and returns a corresponding Facebook URL for each supported language.
   * If the language does not match any of the specified cases, it defaults to returning the US Facebook page URL.
   *
   * @returns {string} The Facebook URL corresponding to the current language setting.
   */
  const getFacebookLink = () => {
    if (i18n.language === "en-us") {
      return "https://www.facebook.com/mpbcomUS/";
    }
    if (i18n.language === "en-uk") {
      return "https://www.facebook.com/mpbcom";
    }
    if (i18n.language === "de-de") {
      return "https://www.facebook.com/mpbcomDE";
    }
    if (i18n.language === "fr-fr") {
      return "https://www.facebook.com/mpbcom";
    }
    if (i18n.language === "en-eu") {
      return "https://www.facebook.com/mpbcom";
    }
    return "https://www.facebook.com/mpbcomUS/";
  };

  /**
   * Checks if there are any votes in the cart by omitting empty values from the votes object and checking if the resulting object is empty.
   *
   * @returns {boolean} True if there are votes in the cart, false otherwise.
   */
  const checkForVotes = () => {
    // Check if we have any votes in the cart
    var has_votes = _.omitBy(votes, _.isEmpty);
    if (_.isEmpty(has_votes)) {
      return false;
    } else {
      return true;
    }
  };

  /**
   * Clear vote button for development - only shows if REACT_APP_TEST_MODE is true and user has voted
   */
  const renderClearVotes = () => {
    if (process.env.REACT_APP_TEST_MODE.toLowerCase() === "true" && voted) {
      return (
        <Box
          style={{
            zIndex: 1001,
            position: "fixed",
            bottom: "100px",
            left: "50px",
          }}
        >
          <Button
            style={{ padding: "10px", fontSize: "14px", lineHeight: "14px" }}
            label="CLEAR YOUR VOTES (DEVELOPMENT)"
            onClick={() => clearVotes()}
          />
        </Box>
      );
    }
  };

  return (
    <Grommet theme={Theme} themeMode="dark">
      {renderClearVotes()}
      <Helmet>{getLanguageMetaTag()}</Helmet>
      <ResponsiveContext.Consumer>
        {(size) => (
          <Box fill>
            <Box
              id="content"
              //flex={{"grow":4, "shrink": 1}}
              //overflow={{"vertical": "scroll"}}
            >
              <MyNav
                id="nav"
                i18n={i18n}
                match={match}
                location={location}
                showSideBar={() => setShowSidebar(!showSidebar)}
                showNav={showNav}
                setShowNav={setShowNav}
                currentYear={currentYear}
              />
              <Box
                className="contentWrapper"
                direction="row"
                flex="grow"
                overflow={{}}
              >
                <CookieBanner
                  message={t("cookie_msg")}
                  wholeDomain={process.env.REACT_APP_SUBFOLDER}
                  policyLink={`${match.url}/privacy-policy`}
                  privacyPolicyLinkText={t("Privacy Policy")}
                  necessaryOptionText={t("Necessary")}
                  preferencesOptionText={t("Preferences")}
                  statisticsOptionText={t("Statistics")}
                  marketingOptionText={t("Marketing")}
                  acceptButtonText={t("Accept all")}
                  declineButtonText={t("Decline")}
                  savePreferencesButtonText={t("Save and close")}
                  managePreferencesButtonText={t("Manage cookies")}
                  onAccept={() => {}}
                  onAcceptPreferences={() => {}}
                  onAcceptStatistics={() => {}}
                  onAcceptMarketing={() => {}}
                />
                <Box
                  className="switchWrapper"
                  width="100%"
                  align="center"
                  justify="start"
                >
                  <ScrollToTop />
                  <Switch>
                    <Route exact path={`${match.url}/`} component={Home} />
                    <Route
                      path={`${match.url}/about-mpb`}
                      render={(props) => (
                        <GenericPage {...props} slug="about-mpb" />
                      )}
                    />
                    <Route
                      path={`${match.url}/press-and-media`}
                      component={Press}
                    />
                    <Route
                      path={`${match.url}/nominating-process`}
                      component={NominatingProcess}
                    />
                    <Route
                      path={`${match.url}/nominees`}
                      component={Categories}
                    />
                    <Route path={`${match.url}/museum`} component={Museum} />
                    <Route
                      path={`${match.url}/privacy-policy`}
                      render={(props) => (
                        <GenericPage {...props} slug="privacy-policy" />
                      )}
                    />
                    <Route
                      path={`${match.url}/terms-and-conditions`}
                      render={(props) => (
                        <GenericPage {...props} slug="terms-and-conditions" />
                      )}
                    />
                    {/*
                    <Route
                      path={`${match.url}/sweepstakes-terms-and-conditions`}
                      render={(props) => (
                        <GenericPage
                          {...props}
                          slug="sweepstakes-terms-and-conditions"
                        />
                      )}
                    />
                    */}
                    <Route
                      path={`${match.url}/test-page`}
                      render={(props) => (
                        <GenericPage {...props} slug="test-page" />
                      )}
                    />
                    <Route
                      path={`${match.url}/not-found`}
                      component={NotFound}
                    />
                    <Route component={NotFound} match={match} />
                  </Switch>
                  <Usher i18n={i18n} match={match} location={location} />
                </Box>
                {!showSidebar || size !== "small" ? (
                  <Collapsible direction="horizontal" open={showSidebar}>
                    <Box
                      width="medium"
                      background="light-2"
                      elevation="small"
                      align="center"
                      justify="center"
                    >
                      sidebar
                    </Box>
                  </Collapsible>
                ) : (
                  <Layer>
                    <Box
                      background="light-2"
                      tag="header"
                      justify="end"
                      align="center"
                      direction="row"
                    >
                      <Button
                        icon={<FormClose />}
                        onClick={() => setShowSidebar(false)}
                      />
                    </Box>
                    <Box
                      fill
                      background="light-2"
                      align="center"
                      justify="center"
                    >
                      sidebar
                    </Box>
                  </Layer>
                )}
              </Box>

              <Footer
                className={checkForVotes() ? "haveVotes" : ""}
                align={smallScreen(size) ? "center" : "end"}
                wrap={true}
                direction={smallScreen(size) ? "column" : "row"}
                justify={smallScreen(size) ? "center" : "start"}
              >
                {/*
                <Box
                  className="footerWinners"
                  justify="center"
                  width="100%"
                  direction="row"
                  gap="medium"
                  style={{marginBottom: "20px"}}
                >
                  <Link to={`${match.url}/class-of-2022`} className="smaller">
                    <Trans>Class of 2022</Trans>
                  </Link>
                  <Link to={`${match.url}/class-of-2021`} className="smaller">
                    <Trans>Class of 2021</Trans>
                  </Link>
                  <Link to={`${match.url}/class-of-2020`} className="smaller">
                    <Trans>Class of 2020</Trans>
                  </Link>
                </Box>
                */}

                <Box
                  className="footerLeft"
                  justify={smallScreen(size) ? "center" : "end"}
                  basis={smallScreen(size) ? "100%" : "45%"}
                  direction={smallScreen(size) ? "row" : "column"}
                  align={smallScreen(size) ? "center" : ""}
                >
                  <img
                    className="footerLogo"
                    src={logo}
                    alt="MPB"
                    style={{
                      width: "100px",
                    }}
                  />

                  <Text className="footerCopyright">© {currentYear} MPB</Text>

                  <div style={{ display: "none" }}>
                    <p>Current locale: {match.params.locale}</p>
                    <p>Current path: {match.path}</p>
                  </div>
                </Box>

                <Box
                  className="footerRight"
                  align={smallScreen(size) ? "center" : "end"}
                  basis={smallScreen(size) ? "100%" : "45%"}
                >
                  <Box direction="row" className="socialLinks">
                    <a target="_blank" href="https://www.instagram.com/mpbcom/">
                      <FontAwesomeIcon icon={faInstagram} />
                    </a>
                    <a target="_blank" href={getFacebookLink()}>
                      <FontAwesomeIcon icon={faFacebook} />
                    </a>
                    <a target="_blank" href="https://www.youtube.com/c/mpbcom">
                      <FontAwesomeIcon icon={faYoutube} />
                    </a>
                    <a target="_blank" href="https://www.tiktok.com/@mpbcom/">
                      <FontAwesomeIcon icon={faTiktok} />
                    </a>
                  </Box>

                  <Box direction="row" className="pageLinks">
                    <LanguageSelector
                      i18n={i18n}
                      match={match}
                      location={location}
                    />
                    <Link to={`${match.url}/about-mpb`}>
                      <Trans>About MPB</Trans>
                    </Link>
                    <Link to={`${match.url}/privacy-policy`}>
                      <Trans>Privacy Policy</Trans>
                    </Link>
                    {SettingsDynamic?.show_sweepstakes_terms &&
                      SettingsDynamic.show_sweepstakes_terms.toLowerCase() ==
                        "true" && (
                        <Link to={`${match.url}/terms-and-conditions`}>
                          <Trans>Terms & Conditions</Trans>
                        </Link>
                      )}
                  </Box>
                </Box>
              </Footer>
            </Box>
            <VoteCart t={t} i18n={i18n} match={match} />
          </Box>
        )}
      </ResponsiveContext.Consumer>
    </Grommet>
  );
};

// Map redux dispatches to props (remember to export below with this function)
const mapDispatchToProps = (dispatch) => {
  return {
    setLocale: (locale) => {
      dispatch(setLocale(locale));
    },
    clearVotes: () => {
      dispatch(clearVotes());
    },
  };
};

/**
 * helper component to strip trailing slashes from url and redirect to correct url - this is to prevent issues with trailing slashes causing contentful preview API to not find content
 * and to generally normalise urls. Only applies to urls with more than 1 character to prevent redirect loops on root url
 */
const RemoveDoubleSlashes = () => {
  const { pathname, search } = useLocation();
  console.debug("Current pathname:", pathname);
  console.debug("Current search:", search);
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return (
      <Redirect
        to={{
          pathname: pathname.replace(/\/+$/, ''),
          search: search
        }}
      />
    );
  }
  
  return null;
};

const mapStateToProps = (state) => ({
  pathname: state.router.location.pathname,
  search: state.router.location.search,
  hash: state.router.location.hash,
  locale: state.cart.locale,
  items: state.cart.items,
  total: state.cart.total,
  votes: state.cart.votes,
  voted: state.cart.voted,
});

App = connect(mapStateToProps, mapDispatchToProps)(App);

const AppTranslated = ({ store, history }) => {
  // 1. Add state to track if the API call has finished
  const [isSettingsLoaded, setSettingsLoaded] = useState(false);

  // 2. Use an effect to fetch data when the app mounts
  useEffect(() => {
    // Call your API function here
  }, []); // Empty dependency array ensures this runs only once

  return (
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <RemoveDoubleSlashes />
          <Switch>
            <Redirect from="/eu" to="/en-eu" />
            <Route
              path="/:locale"
              render={(props) => {
                const { locale } = props.match.params;

                API.getSettings(i18n)
                  .then((data) => {
                    // Convert from array of objects (key, value) to single object
                    const settingsObject = {};
                    data.items.forEach((item) => {
                      settingsObject[item.fields.key] = item.fields.value;
                    });
                    // Dispatch the data to the settings store
                    store.dispatch({
                      type: "SET_SETTINGS",
                      payload: settingsObject,
                    });
                  })
                  .catch((err) => {
                    console.error("Failed to fetch settings data", err);
                    // Handle error (e.g., set an error state or load anyway)
                  })
                  .finally(() => {
                    // 3. Mark the app as ready to render regardless of success/failure
                    // (or only on success if the data is strictly required)
                    setSettingsLoaded(true);
                  });

                // 4. If data isn't loaded yet, show a Loader instead of the App
                // You can reuse the loader logic you had in your Suspense fallback
                if (!isSettingsLoaded) {
                  return (
                    <Box className="suspense">
                      <img
                        src={hof_logo}
                        srcSet={`${hof_logo} 1x, ${hof_logo_2x} 2x, `}
                        alt="The Photo and Video Kit Hall of Fame - Brought to you by MPB"
                        style={{ zIndex: 1000, width: "300px" }}
                      />
                      <Box align="center" direction="row" gap="small">
                        <Spinner
                          color="white"
                          size="small"
                          message="Loading content"
                        />
                        <span>Loading settings</span>
                      </Box>
                    </Box>
                  );
                }

                // Redirect en-gb to en-uk
                if (locale.toLowerCase() === "en-gb") {
                  const newPath = props.location.pathname.replace(
                    props.match.url,
                    "/en-uk",
                  );
                  return (
                    <Redirect
                      to={{
                        pathname: newPath,
                        search: props.location.search, // Preserve query params (e.g. ?sort=date)
                        state: props.location.state, // Preserve location state
                      }}
                    />
                  );
                }

                // Check if the locale contains any uppercase letters
                if (/[A-Z]/.test(locale)) {
                  // Create the new path by replacing the matched part of the URL with its lowercase version.
                  // using props.match.url ensures we only replace the locale segment,
                  // avoiding issues if the locale string appears elsewhere in the path.
                  const newPath = props.location.pathname.replace(
                    props.match.url,
                    props.match.url.toLowerCase(),
                  );

                  return (
                    <Redirect
                      to={{
                        pathname: newPath,
                        search: props.location.search, // Preserve query params (e.g. ?sort=date)
                        state: props.location.state, // Preserve location state
                      }}
                    />
                  );
                }

                // Check if locale is in settings.languages
                // if not redirect to 404
                if (!Settings.languages.includes(locale.toLowerCase())) {
                  return <NotFound />;
                }

                // If already lowercase, render the App as normal
                return <App {...props} />;
              }}
            />
            <Redirect to={"/" + chooseInitialLanguage(history)} />
          </Switch>
        </ConnectedRouter>
      </Provider>
    </I18nextProvider>
  );
};

render(
  <Suspense
    fallback={
      <Box className="suspense" animation="fadeOut">
        <img
          src={hof_logo}
          srcSet={`${hof_logo} 1x, ${hof_logo_2x} 2x, `}
          alt="The Photo and Video Kit Hall of Fame - Brought to you by MPB"
          style={{ zIndex: 1000, width: "300px" }}
        />
        <Box align="center" direction="row" gap="small">
          <Spinner color="white" size="small" message="Loading content" />
          <span>Loading content</span>
        </Box>
      </Box>
    }
  >
    <AppTranslated store={store} history={history} />
  </Suspense>,
  document.getElementById("root"),
  //document.body
);
